/**
 * studioMilestones — detection, persistence, and queue for the
 * editorial milestone celebration system. Source of truth lives in
 * `studio_milestones` (one row per kind per org). Detection runs
 * client-side at app boot and inserts new rows; the overlay reads
 * the queue and marks rows celebrated as it dismisses them.
 *
 * Seeding rule: on the very first detection pass for an org (no
 * existing milestone rows yet), every row we insert is marked
 * `pre_existing = true` so existing studios don't get a parade of
 * celebrations they already lived through.
 */
import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useOrganization } from '@/services/organizations';
import type { NovaState } from '@/components/nova/Nova';

export type MilestoneKind =
  // First-time
  | 'first_inquiry'
  | 'first_client'
  | 'first_project'
  | 'first_payment'
  | 'first_contract_signed'
  | 'first_deliverable'
  | 'first_repeat_client'
  | 'first_studio_site_publish'
  // Count thresholds
  | 'clients_10'
  | 'clients_50'
  | 'clients_100'
  | 'projects_10'
  | 'projects_50'
  | 'projects_100'
  // Invoice / paid threshold
  | 'paid_invoices_10'
  | 'paid_invoices_50'
  // Revenue
  | 'revenue_1k'
  | 'revenue_10k'
  | 'revenue_50k'
  | 'revenue_100k';

export interface MilestoneCopy {
  headline: string;
  subline: string;
  novaState: NovaState;
}

export const MILESTONE_COPY: Record<MilestoneKind, MilestoneCopy> = {
  first_inquiry:             { headline: 'Someone wants to work with you.', subline: 'First inquiry just landed.',                            novaState: 'has-suggestion' },
  first_client:              { headline: 'A real client.',                  subline: 'First name on the books.',                              novaState: 'happy' },
  first_project:             { headline: 'The work begins.',                subline: 'First project is live.',                                 novaState: 'happy' },
  first_payment:             { headline: "Money's in.",                     subline: 'First payment landed in your Stripe account.',           novaState: 'celebrating' },
  first_contract_signed:     { headline: 'Signed and sealed.',              subline: 'First contract just came back signed.',                  novaState: 'happy' },
  first_deliverable:         { headline: 'Delivered.',                      subline: 'First deliverable shared with a client.',                novaState: 'celebrating' },
  first_repeat_client:       { headline: 'They came back.',                 subline: 'First repeat client. Word of mouth starts here.',        novaState: 'celebrating' },
  first_studio_site_publish: { headline: 'Live on the web.',                subline: 'Your Business Site is published.',                       novaState: 'happy' },

  clients_10:                { headline: 'Ten clients deep.',               subline: "You're not testing the waters anymore.",                 novaState: 'happy' },
  clients_50:                { headline: 'Fifty clients.',                  subline: 'A real roster.',                                         novaState: 'celebrating' },
  clients_100:               { headline: 'One hundred clients.',            subline: 'A business with a history.',                             novaState: 'celebrating' },
  projects_10:               { headline: 'Ten projects shipped.',           subline: 'Patterns are forming.',                                  novaState: 'happy' },
  projects_50:               { headline: 'Fifty projects.',                 subline: 'You know your craft.',                                   novaState: 'celebrating' },
  projects_100:              { headline: 'One hundred projects.',           subline: 'A body of work.',                                        novaState: 'celebrating' },

  paid_invoices_10:          { headline: 'Ten invoices paid.',              subline: 'The cash is starting to flow.',                          novaState: 'happy' },
  paid_invoices_50:          { headline: 'Fifty invoices paid.',            subline: 'Reliable revenue.',                                      novaState: 'celebrating' },

  revenue_1k:                { headline: 'First thousand.',                 subline: "Small number, big moment. It's real money now.",         novaState: 'happy' },
  revenue_10k:               { headline: 'Ten thousand earned.',            subline: 'A meaningful chunk of your year.',                       novaState: 'celebrating' },
  revenue_50k:               { headline: 'Fifty thousand.',                 subline: 'A serious creative business.',                           novaState: 'celebrating' },
  revenue_100k:              { headline: 'Six figures.',                    subline: 'You built this.',                                        novaState: 'shocked' },
};

export interface StudioMilestoneRow {
  id: string;
  org_id: string;
  kind: MilestoneKind;
  label: string;
  value: Record<string, unknown>;
  achieved_at: string;
  pre_existing: boolean;
  celebrated_at: string | null;
  created_at: string;
}

// ─── Helpers ────────────────────────────────────────────────────────

const COUNT_THRESHOLDS: Array<{ kind: MilestoneKind; n: number }> = [
  { kind: 'clients_10', n: 10 },
  { kind: 'clients_50', n: 50 },
  { kind: 'clients_100', n: 100 },
];
const PROJECT_THRESHOLDS: Array<{ kind: MilestoneKind; n: number }> = [
  { kind: 'projects_10', n: 10 },
  { kind: 'projects_50', n: 50 },
  { kind: 'projects_100', n: 100 },
];
const PAID_THRESHOLDS: Array<{ kind: MilestoneKind; n: number }> = [
  { kind: 'paid_invoices_10', n: 10 },
  { kind: 'paid_invoices_50', n: 50 },
];
const REVENUE_THRESHOLDS: Array<{ kind: MilestoneKind; cents: number }> = [
  { kind: 'revenue_1k',   cents: 100_000 },
  { kind: 'revenue_10k',  cents: 1_000_000 },
  { kind: 'revenue_50k',  cents: 5_000_000 },
  { kind: 'revenue_100k', cents: 10_000_000 },
];

async function safeCount(table: string, orgId: string): Promise<number> {
  try {
    const { count } = await (supabase.from(table as any) as any)
      .select('*', { count: 'exact', head: true })
      .eq('org_id', orgId);
    return count ?? 0;
  } catch {
    return 0;
  }
}

/**
 * LAUNCH-5 — deliverables have no org_id (only project_id), so
 * `safeCount('deliverables')` asked for a column that isn't there and
 * failed on every signed-in page (MilestoneOverlay runs this in
 * RootLayout). Count them through their project instead, skipping
 * the soft-deleted ones.
 */
async function countDeliverables(orgId: string): Promise<number> {
  try {
    const { count } = await (supabase.from('deliverables') as any)
      .select('id, projects!inner(org_id)', { count: 'exact', head: true })
      .eq('projects.org_id', orgId)
      .is('deleted_at', null);
    return count ?? 0;
  } catch {
    return 0;
  }
}

// ─── Detection ──────────────────────────────────────────────────────

export function useDetectMilestones() {
  const { data: org } = useOrganization();
  const orgId = org?.id;
  const qc = useQueryClient();

  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;

    (async () => {
      try {
        // Pull existing rows + raw signals in parallel.
        const [
          existingRes,
          inquiriesCount,
          clientsCount,
          projectsCount,
          deliverablesCount,
          paymentsRes,
          contractsRes,
          siteRes,
        ] = await Promise.all([
          (supabase.from('studio_milestones') as any)
            .select('kind')
            .eq('org_id', orgId),
          safeCount('inquiries', orgId),
          safeCount('clients', orgId),
          safeCount('projects', orgId),
          countDeliverables(orgId),
          (supabase.from('stripe_payments') as any)
            .select('amount_cents, client_email, status')
            .eq('org_id', orgId)
            .eq('status', 'succeeded'),
          // Contracts has no org_id — join via projects. LAUNCH-5:
          // `document_status` has no 'completed' (draft, sent, viewed,
          // signed, paid), and one unknown value failed the whole read.
          (supabase.from('contracts') as any)
            .select('id, status, projects!inner(org_id)')
            .eq('projects.org_id', orgId)
            .in('status', ['signed', 'paid']),
          (supabase.from('studio_sites') as any)
            .select('published_at')
            .eq('org_id', orgId)
            .maybeSingle(),
        ]);

        if (cancelled) return;

        const existing = new Set<string>(
          ((existingRes as any).data ?? []).map((r: any) => r.kind),
        );
        const isFirstRun = existing.size === 0;

        const payments = ((paymentsRes as any).data ?? []) as Array<{
          amount_cents: number | null;
          client_email: string | null;
        }>;
        const paidCount = payments.length;
        const revenueCents = payments.reduce(
          (sum, p) => sum + (p.amount_cents ?? 0),
          0,
        );
        // Repeat client = same email appears in ≥2 succeeded payments.
        const emailCounts = new Map<string, number>();
        for (const p of payments) {
          const e = (p.client_email ?? '').trim().toLowerCase();
          if (!e) continue;
          emailCounts.set(e, (emailCounts.get(e) ?? 0) + 1);
        }
        const hasRepeatClient = Array.from(emailCounts.values()).some((n) => n >= 2);

        const signedContractsCount = ((contractsRes as any).data ?? []).length;
        const sitePublishedAt =
          ((siteRes as any).data as { published_at?: string | null } | null)?.published_at ?? null;

        // Build candidate rows for every threshold currently crossed.
        type Candidate = { kind: MilestoneKind; label: string; value: Record<string, unknown> };
        const candidates: Candidate[] = [];

        const push = (kind: MilestoneKind, value: Record<string, unknown> = {}) => {
          if (existing.has(kind)) return;
          const copy = MILESTONE_COPY[kind];
          candidates.push({ kind, label: copy.headline, value });
        };

        if (inquiriesCount >= 1) push('first_inquiry');
        if (clientsCount   >= 1) push('first_client');
        if (projectsCount  >= 1) push('first_project');
        if (paidCount      >= 1) push('first_payment', { amount_cents: payments[0]?.amount_cents ?? 0 });
        if (signedContractsCount >= 1) push('first_contract_signed');
        if (deliverablesCount    >= 1) push('first_deliverable');
        if (hasRepeatClient)           push('first_repeat_client');
        if (sitePublishedAt)           push('first_studio_site_publish');

        for (const t of COUNT_THRESHOLDS)   if (clientsCount  >= t.n) push(t.kind, { count: clientsCount });
        for (const t of PROJECT_THRESHOLDS) if (projectsCount >= t.n) push(t.kind, { count: projectsCount });
        for (const t of PAID_THRESHOLDS)    if (paidCount     >= t.n) push(t.kind, { count: paidCount });
        for (const t of REVENUE_THRESHOLDS) if (revenueCents  >= t.cents) push(t.kind, { revenue_cents: revenueCents });

        if (!candidates.length) return;

        const rows = candidates.map((c) => ({
          org_id: orgId,
          kind: c.kind,
          label: c.label,
          value: c.value,
          pre_existing: isFirstRun,
        }));

        const { error } = await (supabase.from('studio_milestones') as any).upsert(rows, {
          onConflict: 'org_id,kind',
          ignoreDuplicates: true,
        });
        if (error) {
          // Silent — RLS or schema drift shouldn't crash the host page.
          return;
        }
        qc.invalidateQueries({ queryKey: ['studio-milestones', 'uncelebrated', orgId] });
      } catch {
        // Detection is best-effort; never throw into the host tree.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [orgId, qc]);
}

// ─── Queue ──────────────────────────────────────────────────────────

export function useUncelebratedMilestone() {
  const { data: org } = useOrganization();
  const orgId = org?.id;

  return useQuery<StudioMilestoneRow | null>({
    queryKey: ['studio-milestones', 'uncelebrated', orgId],
    enabled: !!orgId,
    staleTime: 30_000,
    queryFn: async () => {
      try {
        const { data, error } = await (supabase.from('studio_milestones') as any)
          .select('*')
          .eq('org_id', orgId)
          .eq('pre_existing', false)
          .is('celebrated_at', null)
          .order('achieved_at', { ascending: true })
          .limit(1)
          .maybeSingle();
        if (error) return null;
        return (data as StudioMilestoneRow | null) ?? null;
      } catch {
        return null;
      }
    },
  });
}

export async function markMilestoneCelebrated(id: string): Promise<void> {
  try {
    await (supabase.from('studio_milestones') as any)
      .update({ celebrated_at: new Date().toISOString() })
      .eq('id', id);
  } catch {
    // Silent — if it fails we'll just show it again next load.
  }
}